import React, {useCallback, useState, useEffect, useRef} from 'react'
import { useHistory } from "react-router";

import { usePersistedState, useActionApi } from "hooks";
import { entities } from "reduxStore";
import Api from "api";

import {
	OverviewStep,
	ExitPopup,
	BackendJob,
	EditPostMode,
	EditedJob,
} from "components/EditJob";
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import { JobPostSuccessModal } from 'components/EditPost/JobPostSuccessModal'
import { WritingAssistFlow } from 'components/WritingAssist'
import type {
	WritingAssistGateEnvelope,
	WritingAssistSaveSelection,
} from 'components/WritingAssist'
import {
	isWritingAssistGateUnavailableError,
	writingAssistGateEnvelopeFromError,
} from 'components/WritingAssist'
import ApiError from 'api/ApiError'
import { isWritingAssistV2Enabled } from 'utils/writingAssistV2'

export default function EditJobPostOverviewStep() {
	const history = useHistory()
	const { mode, originalJob } = history.location.state as { mode: EditPostMode, originalJob?: BackendJob }

	const [isExitPopupOpen, setIsExitPopupOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [postedJobData, setPostedJobData] = useState<any>(null);
	const [featureError, setFeatureError] = useState<string>('');
	const [featuredSuccessfully, setFeaturedSuccessfully] = useState<boolean>(false);
	const [writingAssistGate, setWritingAssistGate] = useState<WritingAssistGateEnvelope | null>(null);
	const [writingAssistSelection, setWritingAssistSelection] = useState<WritingAssistSaveSelection | null>(null);
	const [isWritingAssistRecoveryOpen, setIsWritingAssistRecoveryOpen] = useState(false);
	const [writingAssistSubmitLoading, setWritingAssistSubmitLoading] = useState(false);
	const [writingAssistSubmitError, setWritingAssistSubmitError] = useState<ApiError | null>(null);
	const writingAssistSubmitInFlight = useRef(false);
	const writingAssistV2Enabled = isWritingAssistV2Enabled();

	const [persistedJob, saveJob] = usePersistedState({
		entityName: entities.editedJobPost,
		initialValue: {} as EditedJob,
	})
	
	useEffect(() => {
		if (persistedJob && !persistedJob.availability && persistedJob.id) {
			const fixedJob = {...persistedJob};
			
			if (!fixedJob.availability) {
				fixedJob.availability = [0, 0, 0, 0, 0, 0, 0];
			}
			
			saveJob(fixedJob);
		}
	}, [persistedJob, saveJob]);

	const handlePostJobSuccess = async (response: any) => {
			// Fetch the full job details to get the publicUrl
			try {
				const fullJobData = await Api.getHireJob(response.hireJobId);
				setPostedJobData(fullJobData);
			} catch (error) {
				console.error('Failed to fetch job details:', error);
				// Fall back to response, normalizing hireJobId → id for modal compatibility
				setPostedJobData({ ...response, id: response.hireJobId });
			}
		// Enable Featured Listing immediately after posting if selected (Ontario/flag gating handled by API)
		try {
			if ((persistedJob as any)?.featureOnPost && typeof response?.hireJobId === 'number') {
				await Api.featureListing(response.hireJobId);
				setFeaturedSuccessfully(true); // Mark as successfully featured
			}
		} catch (error: any) {
			console.error('[Featured Listing] Failed to enable feature on post:', {
				hireJobId: response.hireJobId,
				error: error.message,
				status: error.status,
				response: error.response?.data,
				timestamp: new Date().toISOString()
			});
			
			// Set error message for display to user
			const errorMsg = error?.response?.data?.error || error?.message || 'Failed to feature listing';
			setFeatureError(`Job posted successfully, but featuring failed: ${errorMsg}. You can feature it later from the job details page.`);
		}
		setIsSuccessModalOpen(true);
	};

	const [jobPostingLoading, jobPostingError, postJobApi] = useActionApi({
		apiMethod: Api.postJob,
		onSuccess: handlePostJobSuccess,
	})
	const [editJobPostingLoading, editJobPostingError, editJobApi] = useActionApi(
		{
			apiMethod: Api.editJob,
			onSuccess: useCallback(() => {
				history.push({ pathname: "jobBoardDetails", state: { job: originalJob } });
			}, [history])
		}
	)

	const onEditJobFlowClose = () => {
		setIsExitPopupOpen(true);
	}

	const onEditJobFlowEdit = () => {
		history.push({ pathname: "editJobFirstStep", state: { mode, originalJob } })
	}
	
	const submitWritingAssistJob = async (selection: WritingAssistSaveSelection) => {
		if (writingAssistSubmitInFlight.current) return undefined;
		writingAssistSubmitInFlight.current = true;
		setWritingAssistSubmitLoading(true);
		setWritingAssistSubmitError(null);
		setWritingAssistSelection(selection);
		const job = { ...persistedJob, description: selection.text };
		try {
			const response = mode === EditPostMode.edit
				? await Api.editJob(job)
				: await Api.postJob(job);
			setWritingAssistGate(null);
			setIsWritingAssistRecoveryOpen(false);
			if (mode === EditPostMode.edit) {
				history.push({ pathname: "jobBoardDetails", state: { job: originalJob } });
			} else {
				await handlePostJobSuccess(response);
			}
			return response;
		} catch (error) {
			const gate = writingAssistGateEnvelopeFromError(error);
			if (gate) {
				setWritingAssistGate(gate);
				setIsWritingAssistRecoveryOpen(true);
			}
			else if (isWritingAssistGateUnavailableError(error)) {
				// Approved WA-L-06 behavior is silent; keep Overview and its exact
				// description available for an explicit retry.
				setWritingAssistGate(null);
				setIsWritingAssistRecoveryOpen(false);
			}
			else {
				setIsWritingAssistRecoveryOpen(false);
				setWritingAssistSubmitError(error as ApiError);
			}
			throw error;
		} finally {
			writingAssistSubmitInFlight.current = false;
			setWritingAssistSubmitLoading(false);
		}
	};

	const onEditJobFlowConfirm = () => {
		let job = persistedJob;
		if (writingAssistV2Enabled) {
			void submitWritingAssistJob({
				text: persistedJob.description || '',
				version: 'original',
			}).catch(() => undefined);
			return;
		}

		if (mode === EditPostMode.edit) {
			editJobApi(job);
		} else {
			postJobApi(job);
		}
	}

	const onExitPopupClose = () => {
		setIsExitPopupOpen(false);
	}
	const onExitPopupConfirm = () => {
		setIsExitPopupOpen(false);

		if (mode === EditPostMode.edit) {
			history.push({ pathname: "jobBoardDetails", state: { job: originalJob } });
		} else {
			history.replace("jobBoard");
		}
	}

	const handleCloseSuccessModal = () => {
		setIsSuccessModalOpen(false);
		setFeaturedSuccessfully(false); // Reset for next job posting in same session
		setFeatureError(''); // Clear any error messages
		history.push("loading");
		history.replace({ pathname: "jobBoard" });
	}
	
	const handlePostTempingShift = () => {
		setIsSuccessModalOpen(false);
		setFeaturedSuccessfully(false); // Reset for next job posting in same session
		setFeatureError(''); // Clear any error messages
		history.push("loading");
		history.push({ pathname: "postJob", state: { fromJobBoard: true } });
	}

	return (
		<>
			{isWritingAssistRecoveryOpen && writingAssistSelection ? (
				<div data-writing-assist-version="v2" data-writing-assist-save-boundary="job-post">
					<WritingAssistFlow
						context="jobDescription"
						entityId={Number(persistedJob.location?.id)}
						initialText={writingAssistSelection.text}
						intervention={writingAssistGate}
						interventionSelection={writingAssistSelection}
						onInterventionChange={setWritingAssistGate}
						save={submitWritingAssistJob}
						onSaveError={error => setWritingAssistSubmitError(error as ApiError)}
					/>
				</div>
			) : <OverviewStep
				job={persistedJob}
				onEdit={onEditJobFlowEdit}
				onClose={onEditJobFlowClose}
				onConfirm={onEditJobFlowConfirm}
				isSubmitting={jobPostingLoading || editJobPostingLoading}
			/>}
			<ExitPopup opened={isExitPopupOpen} onConfirm={onExitPopupConfirm} onClose={onExitPopupClose} />
			<FixedLoader loading={jobPostingLoading || editJobPostingLoading || writingAssistSubmitLoading} />
			<FixedError
				error={writingAssistSubmitError || jobPostingError || editJobPostingError}
				onDismiss={() => setWritingAssistSubmitError(null)}
			/>
		<JobPostSuccessModal 
			isOpen={isSuccessModalOpen} 
			onClose={handleCloseSuccessModal}
			role={persistedJob.position?.id === 0 || persistedJob.position?.id === 1 ? persistedJob.position.id : undefined}
			publicUrl={postedJobData?.publicUrl}
			jobType={persistedJob.position?.id}
			hireJobId={postedJobData?.id}
			onPostTempingShift={handlePostTempingShift}
			featureError={featureError}
			alreadyFeatured={featuredSuccessfully}
		/>
		</>
	)
}
